import React, { useEffect, useState } from "react";
import {
  Tabs,
  Tab,
  Card,
  Row,
  Col,
  Table,
  Button,
  Badge,
  Form,
  Modal,
  Alert,
  Spinner,
  ListGroup,
  Container,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  FaPlus,
  FaTrash,
  FaEdit,
  FaCheck,
  FaTimes,
  FaSave,
} from "react-icons/fa";
import subscriptionService from "../services/subscriptionService";

const SubscriptionManagement = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("users");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userPlans, setUserPlans] = useState([]);
  const [vendorPlans, setVendorPlans] = useState([]);
  const [showEdit, setShowEdit] = useState(false);
  const [editing, setEditing] = useState(null);
  const [newFeature, setNewFeature] = useState("");
  const [showTierEdit, setShowTierEdit] = useState(false);
  const [editingTier, setEditingTier] = useState(null);

  const calcSavings = (price, discountPct) => {
    const p = Number(price) || 0;
    const d = Number(discountPct) || 0;
    const s = (p * d) / 100;
    return Math.round(s * 100) / 100;
  };

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch real subscription plans from API
      const [userPlansData, vendorPlansData] = await Promise.all([
        subscriptionService.getUserPlans(),
        subscriptionService.getVendorPlans(),
      ]);

      setUserPlans(userPlansData);
      setVendorPlans(vendorPlansData);
      setLoading(false);
    } catch (e) {
      console.error("Error fetching plans:", e);
      setError(
        "Failed to load subscription plans from server. Please check your connection and try again."
      );
      setLoading(false);
    }
  };

  const openEdit = (plan, type) => {
    setEditing({ ...plan, type });
    setShowEdit(true);
  };

  const openTierEdit = (tier, plans, type) => {
    // Get quarterly and yearly plans for this tier
    const monthlyPlan = plans.find((p) => p.duration === "Monthly");
    const quarterlyPlan = plans.find((p) => p.duration === "Quarterly");
    const yearlyPlan = plans.find((p) => p.duration === "Yearly");

    setEditingTier({
      tier,
      type,
      monthly: monthlyPlan,
      quarterly: quarterlyPlan,
      yearly: yearlyPlan,
      // Use features from quarterly plan as base (they should be the same)
      features: quarterlyPlan?.features || [],
    });
    setShowTierEdit(true);
  };

  const addFeature = () => {
    if (newFeature.trim() && editing) {
      setEditing({
        ...editing,
        features: [...(editing.features || []), newFeature.trim()],
      });
      setNewFeature("");
    }
  };

  const removeFeature = (index) => {
    if (editing) {
      setEditing({
        ...editing,
        features: editing.features.filter((_, i) => i !== index),
      });
    }
  };

  const saveEdit = async () => {
    if (!editing) return;

    try {
      setLoading(true);

      // Prepare the plan data for update
      const planData = {
        name: editing.name,
        tier: editing.tier,
        price: editing.price,
        duration: editing.duration,
        discountPct: editing.discountPct,
        savings: calcSavings(editing.price, editing.discountPct),
        status: editing.status,
        features: editing.features || [],
      };

      // Call the appropriate API update method
      if (editing.type === "user") {
        await subscriptionService.updateUserPlan(editing.id, planData);
      } else {
        await subscriptionService.updateVendorPlan(editing.id, planData);
      }

      // Refresh the plans from server
      await fetchPlans();

      setShowEdit(false);
      setEditing(null);
      setNewFeature("");
      setLoading(false);

      // Show success message
      alert("Subscription plan updated successfully!");
    } catch (error) {
      console.error("Error saving plan:", error);
      setLoading(false);
      alert("Failed to save subscription plan. Please try again.");
    }
  };

  const saveTierEdit = async () => {
    if (!editingTier) return;

    try {
      setLoading(true);

      // Use the new tier-based update method for features
      if (editingTier.type === "user") {
        await subscriptionService.updateUserTierFeatures(
          editingTier.tier,
          editingTier.features
        );
      } else {
        await subscriptionService.updateVendorTierFeatures(
          editingTier.tier,
          editingTier.features
        );
      }

      // Update individual plan pricing if needed
      const pricingUpdates = [];

      if (editingTier.monthly) {
        const monthlyUpdate = {
          price: editingTier.monthly.price,
          discountPct: editingTier.monthly.discountPct,
          status: editingTier.monthly.status,
          savings: calcSavings(
            editingTier.monthly.price,
            editingTier.monthly.discountPct
          ),
        };

        if (editingTier.type === "user") {
          pricingUpdates.push(
            subscriptionService.updateUserPlan(
              editingTier.monthly.id,
              monthlyUpdate
            )
          );
        } else {
          pricingUpdates.push(
            subscriptionService.updateVendorPlan(
              editingTier.monthly.id,
              monthlyUpdate
            )
          );
        }
      }

      if (editingTier.quarterly) {
        const quarterlyUpdate = {
          price: editingTier.quarterly.price,
          discountPct: editingTier.quarterly.discountPct,
          status: editingTier.quarterly.status,
          savings: calcSavings(
            editingTier.quarterly.price,
            editingTier.quarterly.discountPct
          ),
        };

        if (editingTier.type === "user") {
          pricingUpdates.push(
            subscriptionService.updateUserPlan(
              editingTier.quarterly.id,
              quarterlyUpdate
            )
          );
        } else {
          pricingUpdates.push(
            subscriptionService.updateVendorPlan(
              editingTier.quarterly.id,
              quarterlyUpdate
            )
          );
        }
      }

      if (editingTier.yearly) {
        const yearlyUpdate = {
          price: editingTier.yearly.price,
          discountPct: editingTier.yearly.discountPct,
          status: editingTier.yearly.status,
          savings: calcSavings(
            editingTier.yearly.price,
            editingTier.yearly.discountPct
          ),
        };

        if (editingTier.type === "user") {
          pricingUpdates.push(
            subscriptionService.updateUserPlan(
              editingTier.yearly.id,
              yearlyUpdate
            )
          );
        } else {
          pricingUpdates.push(
            subscriptionService.updateVendorPlan(
              editingTier.yearly.id,
              yearlyUpdate
            )
          );
        }
      }

      // Execute pricing updates
      if (pricingUpdates.length > 0) {
        await Promise.all(pricingUpdates);
      }

      // Refresh the plans from server
      await fetchPlans();

      setShowTierEdit(false);
      setEditingTier(null);
      setNewFeature("");
      setLoading(false);

      // Show success message
      alert("Tier plans updated successfully!");
    } catch (error) {
      console.error("Error saving tier plans:", error);
      setLoading(false);
      alert("Failed to save tier plans. Please try again.");
    }
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "40vh" }}
      >
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger">
        <Alert.Heading>Error</Alert.Heading>
        <p>{error}</p>
        <Button variant="outline-danger" onClick={fetchPlans}>
          Retry
        </Button>
      </Alert>
    );
  }

  const renderPlanCards = (plans, type) => {
    const groupedPlans = plans.reduce((acc, plan) => {
      if (!acc[plan.tier]) {
        acc[plan.tier] = [];
      }
      acc[plan.tier].push(plan);
      return acc;
    }, {});

    // Define the correct order for displaying plans
    const tierOrder = ["basic", "standard", "premium"];

    // Filter and map tiers in the correct order
    return tierOrder
      .filter((tier) => groupedPlans[tier] && groupedPlans[tier].length > 0)
      .map((tier) => {
        const tierPlans = groupedPlans[tier];
        const quarterlyPlan = tierPlans.find((p) => p.duration === "Quarterly");
        const monthlyPlan = tierPlans.find((p) => p.duration === "Monthly");
        const yearlyPlan = tierPlans.find((p) => p.duration === "Yearly");

        return (
          <Card key={tier} className="mb-4">
            <Card.Header
              className={`bg-${
                tier === "basic"
                  ? "primary"
                  : tier === "standard"
                  ? "info"
                  : "success"
              } text-white`}
            >
              <div className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0 text-capitalize">{tier} Plans</h5>
                <Button
                  size="sm"
                  variant="light"
                  onClick={() => openTierEdit(tier, tierPlans, type)}
                >
                  <FaEdit className="me-1" /> Manage Tier
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              <Row>
                {/* Monthly Plan */}
                {monthlyPlan && (
                  <Col md={6} className="mb-3">
                    <Card className="h-100 border-0 shadow-sm">
                      <Card.Body className="text-center">
                        <h6 className="card-title text-capitalize">
                          {monthlyPlan.name}
                        </h6>
                        <div className="mb-2">
                          <Badge bg="primary">Monthly</Badge>
                        </div>
                        <h4 className="text-success mb-2">
                          ₹{monthlyPlan.price}
                        </h4>
                        {monthlyPlan.discountPct > 0 && (
                          <div className="mb-2">
                            <small className="text-muted">
                              Save ₹{monthlyPlan.savings}
                            </small>
                            <br />
                            <Badge bg="warning" text="dark">
                              {monthlyPlan.discountPct}% OFF
                            </Badge>
                          </div>
                        )}
                        <div className="mb-3">
                          <Badge
                            bg={
                              monthlyPlan.status === "active"
                                ? "success"
                                : "secondary"
                            }
                          >
                            {monthlyPlan.status.toUpperCase()}
                          </Badge>
                        </div>
                        <div className="mb-3">
                          <small className="text-muted">
                            {monthlyPlan.features
                              ? monthlyPlan.features.length + " features"
                              : "0 features"}
                          </small>
                        </div>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => openEdit(monthlyPlan, type)}
                          className="w-100 mb-2"
                        >
                          <FaEdit className="me-1" /> Edit Pricing
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                )}

                {/* Quarterly Plan */}
                {quarterlyPlan && (
                  <Col md={6} className="mb-3">
                    <Card className="h-100 border-0 shadow-sm">
                      <Card.Body className="text-center">
                        <h6 className="card-title text-capitalize">
                          {quarterlyPlan.name}
                        </h6>
                        <div className="mb-2">
                          <Badge bg="info">Quarterly</Badge>
                        </div>
                        <h4 className="text-success mb-2">
                          ₹{quarterlyPlan.price}
                        </h4>
                        {quarterlyPlan.discountPct > 0 && (
                          <div className="mb-2">
                            <small className="text-muted">
                              Save ₹{quarterlyPlan.savings}
                            </small>
                            <br />
                            <Badge bg="warning" text="dark">
                              {quarterlyPlan.discountPct}% OFF
                            </Badge>
                          </div>
                        )}
                        <div className="mb-3">
                          <Badge
                            bg={
                              quarterlyPlan.status === "active"
                                ? "success"
                                : "secondary"
                            }
                          >
                            {quarterlyPlan.status.toUpperCase()}
                          </Badge>
                        </div>
                        <div className="mb-3">
                          <small className="text-muted">
                            {quarterlyPlan.features
                              ? quarterlyPlan.features.length + " features"
                              : "0 features"}
                          </small>
                        </div>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => openEdit(quarterlyPlan, type)}
                          className="w-100 mb-2"
                        >
                          <FaEdit className="me-1" /> Edit Pricing
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                )}

                {/* Yearly Plan */}
                {yearlyPlan && (
                  <Col md={6} className="mb-3">
                    <Card className="h-100 border-0 shadow-sm">
                      <Card.Body className="text-center">
                        <h6 className="card-title text-capitalize">
                          {yearlyPlan.name}
                        </h6>
                        <div className="mb-2">
                          <Badge bg="success">Yearly</Badge>
                        </div>
                        <h4 className="text-success mb-2">
                          ₹{yearlyPlan.price}
                        </h4>
                        {yearlyPlan.discountPct > 0 && (
                          <div className="mb-2">
                            <small className="text-muted">
                              Save ₹{yearlyPlan.savings}
                            </small>
                            <br />
                            <Badge bg="warning" text="dark">
                              {yearlyPlan.discountPct}% OFF
                            </Badge>
                          </div>
                        )}
                        <div className="mb-3">
                          <Badge
                            bg={
                              yearlyPlan.status === "active"
                                ? "success"
                                : "secondary"
                            }
                          >
                            {yearlyPlan.status.toUpperCase()}
                          </Badge>
                        </div>
                        <div className="mb-3">
                          <small className="text-muted">
                            {yearlyPlan.features
                              ? yearlyPlan.features.length + " features"
                              : "0 features"}
                          </small>
                        </div>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => openEdit(yearlyPlan, type)}
                          className="w-100 mb-2"
                        >
                          <FaEdit className="me-1" /> Edit Pricing
                        </Button>
                      </Card.Body>
                    </Card>
                  </Col>
                )}
              </Row>

              {/* Features Preview */}
              <div className="mt-3">
                <h6 className="text-muted mb-2">Features:</h6>
                <div className="feature-preview">
                  {quarterlyPlan?.features
                    ?.slice(0, 5)
                    .map((feature, index) => (
                      <Badge
                        key={index}
                        bg="light"
                        text="dark"
                        className="me-1 mb-1"
                      >
                        {feature}
                      </Badge>
                    )) || (
                    <span className="text-muted">No features defined</span>
                  )}
                  {quarterlyPlan?.features?.length > 5 && (
                    <Badge bg="secondary" className="me-1 mb-1">
                      +{quarterlyPlan.features.length - 5} more
                    </Badge>
                  )}
                </div>
              </div>
            </Card.Body>
          </Card>
        );
      });
  };

  return (
    <Container fluid className="px-3 px-md-4 py-3">
      <div className="d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-2 mb-4">
        <h2 className="mb-0 fw-bold">Subscription Management</h2>
        <div>
          <Button variant="outline-secondary" onClick={() => navigate(-1)}>
            Back
          </Button>
        </div>
      </div>

      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k || "users")}
        className="mb-4"
      >
        <Tab eventKey="users" title="User Subscriptions">
          {renderPlanCards(userPlans, "user")}
        </Tab>

        <Tab eventKey="vendors" title="Vendor Subscriptions">
          {renderPlanCards(vendorPlans, "vendor")}
        </Tab>
      </Tabs>

      {/* Individual Plan Edit Modal */}
      <Modal
        show={showEdit}
        onHide={() => setShowEdit(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Edit {editing?.name} Plan - {editing?.duration}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editing && (
            <Form>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Name</Form.Label>
                    <Form.Control
                      value={editing.name}
                      onChange={(e) =>
                        setEditing({ ...editing, name: e.target.value })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Duration</Form.Label>
                    <Form.Select
                      value={editing.duration}
                      onChange={(e) =>
                        setEditing({ ...editing, duration: e.target.value })
                      }
                    >
                      <option>Monthly</option>
                      <option>Quarterly</option>
                      <option>Yearly</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>
              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Price (₹)</Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      value={editing.price}
                      onChange={(e) =>
                        setEditing({
                          ...editing,
                          price: parseFloat(e.target.value) || 0,
                        })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Discount (%)</Form.Label>
                    <Form.Control
                      type="number"
                      step="1"
                      value={editing.discountPct}
                      onChange={(e) =>
                        setEditing({
                          ...editing,
                          discountPct: parseInt(e.target.value || "0", 10),
                        })
                      }
                    />
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      value={editing.status}
                      onChange={(e) =>
                        setEditing({ ...editing, status: e.target.value })
                      }
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Alert variant="info">
                <strong>Note:</strong> To manage features for this tier, use the
                "Manage Tier" button. Feature changes will apply to both
                quarterly and yearly plans.
              </Alert>

              <Form.Group className="mb-3">
                <Form.Label>Current Features (Read Only)</Form.Label>
                <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                  {editing.features && editing.features.length > 0 ? (
                    <ListGroup>
                      {editing.features.map((feature, index) => (
                        <ListGroup.Item key={index}>{feature}</ListGroup.Item>
                      ))}
                    </ListGroup>
                  ) : (
                    <p className="text-muted">
                      No features defined for this tier.
                    </p>
                  )}
                </div>
              </Form.Group>
            </Form>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEdit(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={saveEdit}>
            <FaSave className="me-1" /> Save Pricing Changes
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Tier Management Modal */}
      <Modal
        show={showTierEdit}
        onHide={() => setShowTierEdit(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Manage {editingTier?.tier} Tier - Features & Pricing
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editingTier && (
            <Container fluid>
              <Row>
                {/* Features Management */}
                <Col md={6}>
                  <Card className="h-100">
                    <Card.Header>
                      <h6 className="mb-0">Shared Features</h6>
                      <small className="text-muted">
                        Changes apply to both quarterly and yearly plans
                      </small>
                    </Card.Header>
                    <Card.Body>
                      <Form.Group className="mb-3">
                        <Form.Label>Add New Feature</Form.Label>
                        <div className="input-group">
                          <Form.Control
                            placeholder="Enter feature description..."
                            value={newFeature}
                            onChange={(e) => setNewFeature(e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                if (newFeature.trim()) {
                                  setEditingTier({
                                    ...editingTier,
                                    features: [
                                      ...editingTier.features,
                                      newFeature.trim(),
                                    ],
                                  });
                                  setNewFeature("");
                                }
                              }
                            }}
                          />
                          <Button
                            variant="outline-primary"
                            onClick={() => {
                              if (newFeature.trim()) {
                                setEditingTier({
                                  ...editingTier,
                                  features: [
                                    ...editingTier.features,
                                    newFeature.trim(),
                                  ],
                                });
                                setNewFeature("");
                              }
                            }}
                          >
                            <FaPlus />
                          </Button>
                        </div>
                      </Form.Group>

                      <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                        <ListGroup>
                          {editingTier.features.map((feature, index) => (
                            <ListGroup.Item
                              key={index}
                              className="d-flex justify-content-between align-items-center"
                            >
                              <span>{feature}</span>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() => {
                                  setEditingTier({
                                    ...editingTier,
                                    features: editingTier.features.filter(
                                      (_, i) => i !== index
                                    ),
                                  });
                                }}
                              >
                                <FaTrash />
                              </Button>
                            </ListGroup.Item>
                          ))}
                          {editingTier.features.length === 0 && (
                            <ListGroup.Item className="text-muted text-center">
                              No features added yet
                            </ListGroup.Item>
                          )}
                        </ListGroup>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>

                {/* Pricing Management */}
                <Col md={6}>
                  <Card className="h-100">
                    <Card.Header>
                      <h6 className="mb-0">Pricing Configuration</h6>
                      <small className="text-muted">
                        Set different prices for quarterly and yearly plans
                      </small>
                    </Card.Header>
                    <Card.Body>
                      {/* Monthly Pricing */}
                      {editingTier.monthly && (
                        <Card className="mb-3 border-primary">
                          <Card.Header className="bg-primary text-white">
                            <h6 className="mb-0">Monthly Plan</h6>
                          </Card.Header>
                          <Card.Body>
                            <Row>
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Price (₹)</Form.Label>
                                  <Form.Control
                                    type="number"
                                    step="0.01"
                                    value={editingTier.monthly.price}
                                    onChange={(e) =>
                                      setEditingTier({
                                        ...editingTier,
                                        monthly: {
                                          ...editingTier.monthly,
                                          price:
                                            parseFloat(e.target.value) || 0,
                                        },
                                      })
                                    }
                                  />
                                </Form.Group>
                              </Col>
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Discount (%)</Form.Label>
                                  <Form.Control
                                    type="number"
                                    step="1"
                                    value={editingTier.monthly.discountPct}
                                    onChange={(e) =>
                                      setEditingTier({
                                        ...editingTier,
                                        monthly: {
                                          ...editingTier.monthly,
                                          discountPct: parseInt(
                                            e.target.value || "0",
                                            10
                                          ),
                                        },
                                      })
                                    }
                                  />
                                </Form.Group>
                              </Col>
                            </Row>
                            <Form.Group className="mb-3">
                              <Form.Label>Status</Form.Label>
                              <Form.Select
                                value={editingTier.monthly.status}
                                onChange={(e) =>
                                  setEditingTier({
                                    ...editingTier,
                                    monthly: {
                                      ...editingTier.monthly,
                                      status: e.target.value,
                                    },
                                  })
                                }
                              >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                              </Form.Select>
                            </Form.Group>
                          </Card.Body>
                        </Card>
                      )}
                      {/* Quarterly Pricing */}
                      {editingTier.quarterly && (
                        <Card className="mb-3 border-info">
                          <Card.Header className="bg-info text-white">
                            <h6 className="mb-0">Quarterly Plan</h6>
                          </Card.Header>
                          <Card.Body>
                            <Row>
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Price (₹)</Form.Label>
                                  <Form.Control
                                    type="number"
                                    step="0.01"
                                    value={editingTier.quarterly.price}
                                    onChange={(e) =>
                                      setEditingTier({
                                        ...editingTier,
                                        quarterly: {
                                          ...editingTier.quarterly,
                                          price:
                                            parseFloat(e.target.value) || 0,
                                        },
                                      })
                                    }
                                  />
                                </Form.Group>
                              </Col>
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Discount (%)</Form.Label>
                                  <Form.Control
                                    type="number"
                                    step="1"
                                    value={editingTier.quarterly.discountPct}
                                    onChange={(e) =>
                                      setEditingTier({
                                        ...editingTier,
                                        quarterly: {
                                          ...editingTier.quarterly,
                                          discountPct: parseInt(
                                            e.target.value || "0",
                                            10
                                          ),
                                        },
                                      })
                                    }
                                  />
                                </Form.Group>
                              </Col>
                            </Row>
                            <Form.Group className="mb-3">
                              <Form.Label>Status</Form.Label>
                              <Form.Select
                                value={editingTier.quarterly.status}
                                onChange={(e) =>
                                  setEditingTier({
                                    ...editingTier,
                                    quarterly: {
                                      ...editingTier.quarterly,
                                      status: e.target.value,
                                    },
                                  })
                                }
                              >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                              </Form.Select>
                            </Form.Group>
                          </Card.Body>
                        </Card>
                      )}

                      {/* Yearly Pricing */}
                      {editingTier.yearly && (
                        <Card className="border-success">
                          <Card.Header className="bg-success text-white">
                            <h6 className="mb-0">Yearly Plan</h6>
                          </Card.Header>
                          <Card.Body>
                            <Row>
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Price (₹)</Form.Label>
                                  <Form.Control
                                    type="number"
                                    step="0.01"
                                    value={editingTier.yearly.price}
                                    onChange={(e) =>
                                      setEditingTier({
                                        ...editingTier,
                                        yearly: {
                                          ...editingTier.yearly,
                                          price:
                                            parseFloat(e.target.value) || 0,
                                        },
                                      })
                                    }
                                  />
                                </Form.Group>
                              </Col>
                              <Col md={6}>
                                <Form.Group className="mb-3">
                                  <Form.Label>Discount (%)</Form.Label>
                                  <Form.Control
                                    type="number"
                                    step="1"
                                    value={editingTier.yearly.discountPct}
                                    onChange={(e) =>
                                      setEditingTier({
                                        ...editingTier,
                                        yearly: {
                                          ...editingTier.yearly,
                                          discountPct: parseInt(
                                            e.target.value || "0",
                                            10
                                          ),
                                        },
                                      })
                                    }
                                  />
                                </Form.Group>
                              </Col>
                            </Row>
                            <Form.Group className="mb-3">
                              <Form.Label>Status</Form.Label>
                              <Form.Select
                                value={editingTier.yearly.status}
                                onChange={(e) =>
                                  setEditingTier({
                                    ...editingTier,
                                    yearly: {
                                      ...editingTier.yearly,
                                      status: e.target.value,
                                    },
                                  })
                                }
                              >
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                              </Form.Select>
                            </Form.Group>
                          </Card.Body>
                        </Card>
                      )}
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Container>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTierEdit(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={saveTierEdit}>
            <FaSave className="me-1" /> Save Tier Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default SubscriptionManagement;
